import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export type WorkoutMetricType =
  | 'heart_rate'
  | 'speed'
  | 'cadence'
  | 'power'
  | 'altitude'
  | 'temperature'
  | 'skin_temperature'
  | 'heat_strain_index'
  | 'distance'

export interface WorkoutMetricRequest {
  name: WorkoutMetricType
  label: string
  unit: string
  color: string
  smoothingWindow: number
  downsampleInterval: number
}

export interface WorkoutMetricResponse {
  label?: string
  unit?: string
  color?: string
  name: WorkoutMetricType
  smoothingWindow: number
  downsampleInterval: number
  samples: WorkoutMetricSample[]
}

export interface WorkoutMetric {
  name: WorkoutMetricType
  label: string
  unit: string
  color: string
  samples: { timestamp: number; value: number | null }[]
}

type WorkoutMetricSample = {
  timestamp: number
  value: number | null
}

export function useWorkoutMetrics(
  workoutId: string | undefined,
  metricName: WorkoutMetricType,
  options?: { downsampleInterval: 5; smoothingWindow: 5 }
) {
  return useQuery<WorkoutMetricResponse>({
    queryKey: ['get_workout_metrics', { workoutId, metricName }],
    enabled: workoutId !== undefined,
    queryFn: async () => {
      console.log('useWorkoutMetrics: ', { workoutId, metricName, options })
      const params: any = {
        p_workout_id: workoutId!,
        p_metric_name: metricName,
        p_downsample_interval: undefined,
        p_smoothing_window: undefined,
      }
      if (options?.downsampleInterval) params.p_downsample_interval = options.downsampleInterval
      if (options?.smoothingWindow) params.p_smoothing_window = options.smoothingWindow
      const { data, error } = await supabase.rpc('get_workout_metrics', params)
      if (error) {
        throw error
      }
      const samples = data?.map((sample) => ({
        timestamp: sample.timestamp_seconds,
        value: sample.value,
      }))
      return {
        name: metricName,
        downsampleInterval: params?.downsampleInterval,
        smoothingWindow: params?.smoothingWindow,
        samples,
      } as WorkoutMetricResponse
    },
  })
}

const fetchSingleMetrics = async (workoutId: string, metric: WorkoutMetricRequest) => {
  const { data, error } = await supabase.rpc('get_workout_metrics', {
    p_workout_id: workoutId,
    p_metric_name: metric.name,
    p_downsample_interval: metric.downsampleInterval,
    p_smoothing_window: metric.smoothingWindow,
  })
  if (error) {
    throw error
  }

  const samples = data?.map((sample) => ({
    timestamp: sample.timestamp_seconds,
    value: sample.value,
  }))

  return {
    ...metric,
    samples: samples || [],
  } as WorkoutMetricResponse
}

const fetchMetrics = async (workoutId: string, metrics: WorkoutMetricRequest[]) => {
  return await Promise.all(
    metrics.map(async (metric) => {
      return fetchSingleMetrics(workoutId, metric)
    })
  )
}

export function useWorkoutMetricsGroup(workoutId: string | undefined, metrics: WorkoutMetricRequest[]) {
  const { data, isFetching, isLoading, error } = useQuery<WorkoutMetricResponse[]>({
    queryKey: [
      'workout-metrics',
      workoutId,
      metrics.map((metric) => [metric.name, metric.downsampleInterval, metric.smoothingWindow]),
    ],
    enabled: workoutId !== undefined && Boolean(workoutId && metrics.length > 0),
    queryFn: async () => {
      console.log('useWorkoutMetricsGroup: ', { workoutId, metrics })
      const results: WorkoutMetricResponse[] = await fetchMetrics(workoutId!, metrics)
      return results.filter((metric) => metric !== null)
    },
  })
  return {
    data,
    isLoading,
    isFetching,
    error,
  }
}
